import { DownOutlined, SearchOutlined } from "@ant-design/icons"
import { Button, Checkbox, Empty, Flex, Popover } from "antd"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { DebouncedInput } from "../../../../components/Input/DebouncedInput.tsx"
import { cn } from "../../../../utils/cn.ts"

export interface FilterMenuOption {
  label: string
  value: string
}

interface FilterMenuButtonProps {
  label: string
  options: FilterMenuOption[]
  value: string[]
  onChange: (next: string[]) => void
  /** A search box at the top; worth it once a list stops fitting at a glance. */
  searchable?: boolean
  className?: string
}

/**
 * A toolbar filter that keeps its own name. The choices live in the popover and
 * in the chip row under the toolbar, so the control never repeats them — it
 * says "Status" and how many are picked.
 */
export function FilterMenuButton({
  label,
  options,
  value,
  onChange,
  searchable = options.length > 8,
  className
}: FilterMenuButtonProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase()
    return term
      ? options.filter((option) => option.label.toLowerCase().includes(term))
      : options
  }, [options, search])

  const count = value.length

  const panel = (
    <Flex vertical gap={8} className="w-64 max-w-[80vw]">
      {searchable ? (
        <DebouncedInput
          autoFocus
          allowClear
          delay={0}
          prefix={<SearchOutlined className="text-slate-500" />}
          placeholder={t("projectsCommand.filters.searchIn", { label })}
          aria-label={t("projectsCommand.filters.searchIn", { label })}
          value={search}
          onValueChange={(next) => setSearch(next ?? "")}
        />
      ) : null}
      {visible.length ? (
        <Checkbox.Group
          aria-label={label}
          className="flex max-h-64 flex-col gap-1 overflow-auto"
          value={value}
          onChange={(next) => onChange(next.map(String))}
          options={visible.map((option) => ({
            label: option.label,
            value: option.value
          }))}
        />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("projectsCommand.filters.noOptions")}
        />
      )}
      {count ? (
        <Button
          type="link"
          size="small"
          className="self-start px-0"
          onClick={() => onChange([])}
        >
          {t("projectsCommand.filters.clearOne", { label })}
        </Button>
      ) : null}
    </Flex>
  )

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setSearch("")
      }}
      trigger="click"
      placement="bottomLeft"
      content={panel}
      arrow={false}
    >
      <Button
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={
          count
            ? t("projectsCommand.filters.withCount", { label, count })
            : label
        }
        className={cn(
          "inline-flex items-center justify-between gap-2 border-slate-300 text-slate-800 hover:!border-teal-600",
          count && "border-teal-600 text-teal-800",
          className
        )}
      >
        <span className="inline-flex items-center gap-2">
          {label}
          {count ? (
            <span className="rounded-full bg-teal-50 px-1.5 text-xs font-semibold leading-5 text-teal-800 tabular-nums">
              {count}
            </span>
          ) : null}
        </span>
        <DownOutlined aria-hidden className="text-[10px] text-slate-600" />
      </Button>
    </Popover>
  )
}
